import { createSelector } from 'reselect';
import moment from 'moment';
import {
  EVENT_START_PREFIX,
  EVENT_END_PREFIX,
  EVENT_PRESALE_BOTTOM_CONTENT1,
  EVENT_PRESALE_BOTTOM_CONTENT2,
  EVENT_ACTIVESALE_BOTTOM_CONTENT1,
  COLOUR_FLAG_HANGING_BANNER,
  TEST_SALE_ICON,
  EVENT_CARD_DEFAULT_DATA,
  eventsWinnersPage
} from '../pages/TopShopperPage/Constants';
import { getDayString } from '../pages/TopShopperPage/utils/TopShopperDateUtils';

const getTopShopperData = (state: { topShoppers: any }) => state.topShoppers;

const getGrandEventData = createSelector(getTopShopperData, topShoppers => topShoppers.grandEvent);

const getEventData = createSelector(getTopShopperData, topShoppers => topShoppers.event);

const getAppWebViewStatus = createSelector(
  getTopShopperData,
  topShoppers => topShoppers.appWebViewStatus
);

const getSaleType = createSelector(getEventData, ({ saleType }) => saleType);

const getDataForDateDropdown = createSelector(getEventData, ({ activeDays }) => {
  return activeDays?.reduce((acc: { value: any; label: string }[], currDay: moment.MomentInput) => {
    const datelabel = getDayString(currDay);
    acc.push({
      value: currDay,
      label: datelabel
    });
    return acc;
  }, []);
});

const getEventCardData = (
  saleType: string,
  eventObject: Record<string, unknown>
): Record<string, unknown> => {
  if (saleType === 'PRE_SALE') {
    const { description = '', bannerImageURL = '', nextScheduleStart = 0 } = eventObject || {};
    return {
      eventType: saleType,
      eventLabel: '',
      eventDescripton: description,
      eventStartsIn: nextScheduleStart,
      eventBannerUrl: COLOUR_FLAG_HANGING_BANNER,
      eventIcon: bannerImageURL,
      eventTimerPrefix: EVENT_START_PREFIX,
      ctaLabel: 'View Events & Rewards'
    };
  }
  if (saleType === 'ACTIVE_SALE') {
    const {
      activeSlot: {
        slot: { endTime = 0 } = {},
        offer: { bannerImageURL = '', label = '', description = '' } = {}
      } = {}
    } = eventObject || {};
    return {
      eventType: saleType,
      eventLabel: description,
      eventDescripton: label,
      eventEndsIn: endTime,
      eventBannerUrl: COLOUR_FLAG_HANGING_BANNER,
      eventIcon: bannerImageURL,
      eventTimerPrefix: EVENT_END_PREFIX,
      ctaLabel: 'Shop More'
    };
  }
  if (saleType === 'POST_SALE') {
    const { bannerImageURL = '' } = eventObject || {};
    return {
      eventType: saleType,
      eventBannerUrl: COLOUR_FLAG_HANGING_BANNER,
      eventIcon: bannerImageURL
    };
  }
  return {};
};

const getUpcomingEventsData = createSelector(
  getEventData,
  (eventObject: Record<string, unknown>): any[] => {
    const { upcomingSlots = [] } = eventObject || {};
    if (Array.isArray(upcomingSlots) && upcomingSlots.length) {
      return [...upcomingSlots];
    }
    return [];
  }
);

const getLeaderBoardData = createSelector(
  getEventData,
  getSaleType,
  (eventObject: Record<string, unknown>, saleType: string): [] => {
    if (saleType === 'PRE_SALE' || saleType === 'POST_SALE') {
      const { pastSlots = [] } = eventObject || {};
      if (Array.isArray(pastSlots) && pastSlots.length) {
        return pastSlots.slice(-3).reverse();
      }
      return [];
    }
    if (saleType === 'ACTIVE_SALE') {
      const { activeSlot = {}, pastSlots = [] } = eventObject || {};
      if (
        Object.prototype.toString.call(activeSlot) === '[object Object]' &&
        Array.isArray(pastSlots) &&
        pastSlots.length
      ) {
        return [{ ...activeSlot }, ...pastSlots.slice(-2).reverse()];
      }
      if (
        Object.prototype.toString.call(activeSlot) !== '[object Object]' &&
        Array.isArray(pastSlots) &&
        pastSlots.length
      ) {
        return pastSlots.slice(-3).reverse();
      }

      if (Object.prototype.toString.call(activeSlot) === '[object Object]') {
        return [{ ...activeSlot }];
      }
      return [];
    }
    return [];
  }
);

const getEventLayoutComponentsData = createSelector(
  getEventData,
  getSaleType,
  getUpcomingEventsData,
  getLeaderBoardData,
  (eventObject, saleType, upcomingEventsData, leaderBoardData) => {
    return {
      eventCardData: getEventCardData(saleType, eventObject),
      upcomingEventsData,
      leaderBoardData
    };
  }
);

export {
  getAppWebViewStatus,
  getEventCardData,
  getEventData,
  getDataForDateDropdown,
  getSaleType,
  getEventLayoutComponentsData
};
